import { jsPDF } from 'jspdf';
import { clean, projectStem } from './exporters';
import { mapShot } from './mapShot';
import { aiBook, photoNote } from './photoAi';
import { photosOf } from './photoReportPdf';

// all photos of a project / day -> AI narrated book (report, story) or printable photo album
const W = 210, H = 297, M = 16, CW = W - 2 * M;
const fit = (doc, src, x, y, w, h, cover = false) => {
  try {
    const p = doc.getImageProperties(src), s = cover ? Math.max(w / p.width, h / p.height) : Math.min(w / p.width, h / p.height), iw = p.width * s, ih = p.height * s;
    if (cover) { doc.saveGraphicsState(); doc.rect(x, y, w, h, null); doc.clip(); doc.discardPath(); doc.addImage(src, 'JPEG', x + (w - iw) / 2, y + (h - ih) / 2, iw, ih); doc.restoreGraphicsState(); return h; }
    doc.addImage(src, 'JPEG', x + (w - iw) / 2, y, iw, ih); return ih;
  } catch { return 0; }
};
const hhmm = (ms, lang) => (ms ? new Date(ms).toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' }) : '');

export async function photoBook(project, t, lang, style) {
  const reps = (project.reports || []).filter((r) => photosOf(r).length);
  const chapters = reps.map((r) => { const ph = photosOf(r), ts = ph.map((p) => p.takenAt || p.createdAt).filter(Boolean); return { title: r.title, place: r.address || '', when: ts.length ? `${hhmm(Math.min(...ts), lang)}–${hhmm(Math.max(...ts), lang)}` : '', photos: ph.map((p) => [photoNote(p), p.aiDesc].filter(Boolean).join(' | ') || '-') }; });
  if (!chapters.length) throw new Error('empty');
  const act = project.docSector === 'leisure' ? t[`fxA_${project.activity}`] || '' : t[`fxS_${project.docSector}`] || '';
  const ai = await aiBook(style, project.title, act, chapters);
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  let y = M, k = 0;
  const text = (s, size, bold = false, color = [30, 30, 30]) => {
    if (!s) return;
    doc.setFont('times', bold ? 'bold' : 'normal'); doc.setFontSize(size); doc.setTextColor(...color);
    doc.splitTextToSize(clean(s), CW).forEach((ln) => { if (y > H - M) { doc.addPage(); y = M; } doc.text(ln, M, y); y += size * 0.47; });
    y += 2;
  };
  // cover
  const all = reps.flatMap(photosOf), cover = all.find((p) => p.role === 'main') || all[0];
  fit(doc, cover.data, 0, 0, W, 200, true);
  doc.setFillColor(15, 23, 42); doc.rect(0, 200, W, 97, 'F');
  doc.setTextColor(255, 255, 255); doc.setFont('times', 'bold'); doc.setFontSize(28);
  doc.text(doc.splitTextToSize(clean(ai.title || project.title), CW).slice(0, 2), M, 225);
  doc.setFont('times', 'italic'); doc.setFontSize(13);
  doc.text(clean([act, new Date(project.createdAt || Date.now()).toLocaleDateString(lang, { dateStyle: 'long' }), `${all.length} ${all.length === 1 ? t.fxPhoto : t.fxPhotos}`].filter(Boolean).join(' · ')), M, 250);
  doc.addPage(); y = M + 6;
  text(ai.intro, 13);
  reps.forEach((r, ci) => {
    const ch = ai.chapters?.[ci] || {}, ph = photosOf(r);
    if (style === 'print') {
      doc.addPage(); y = M;
      text(ch.title || r.title, 17, true, [15, 23, 42]); text(ch.text, 11, false, [70, 70, 70]);
      ph.forEach((p, i) => {
        const slot = i % 4; if (i && !slot) { doc.addPage(); y = M; }
        const top = (i < 4 ? y : M) + Math.floor(slot / 2) * 125, x = M + (slot % 2) * (CW / 2 + 2);
        fit(doc, p.data, x, top, CW / 2 - 2, 105, true);
        doc.setFont('times', 'italic'); doc.setFontSize(10); doc.setTextColor(60, 60, 60);
        doc.text(doc.splitTextToSize(clean(ai.captions?.[k] || p.caption || ''), CW / 2 - 4).slice(0, 2), x, top + 111); k++;
      });
      return;
    }
    if (y > H - 80) { doc.addPage(); y = M; }
    y += 4; text(ch.title || r.title, 18, true, [15, 23, 42]); text(ch.text, 12);
    ph.forEach((p) => {
      if (y > H - 120) { doc.addPage(); y = M; }
      y += fit(doc, p.data, M, y, CW, 110) + 3;
      doc.setFont('times', 'italic'); doc.setFontSize(10.5); doc.setTextColor(80, 80, 80);
      doc.splitTextToSize(clean(ai.captions?.[k] || p.caption || ''), CW).slice(0, 3).forEach((ln) => { doc.text(ln, M, y); y += 4.8; });
      y += 5; k++;
    });
  });
  doc.addPage(); y = M + 6;
  text(ai.outro, 13);
  const pins = reps.map((r, i) => ({ lat: r.lat ?? project.lat, lon: r.lon ?? project.lon, label: i + 1 })).filter((p) => p.lat != null);
  const mp = pins.length ? await mapShot({ src: 'osm', zmax: 16, pins }) : null;
  if (mp) fit(doc, mp, M, y + 4, CW, 120);
  return { blob: doc.output('blob'), name: `${projectStem(project)}_${style === 'story' ? 'Story' : style === 'print' ? 'Fotobuch' : 'Bildbericht'}.pdf` };
}
